import { Inject, Injectable } from '@angular/core';
import { TranslateLoader, TranslateService, TranslationObject } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { TRANSLATION_FALLBACK_LANGS } from './translation-fallback.tokens';

/**
 * Готовит цепочку фолбэка переводов и активирует её.
 *
 * Здесь собраны все нюансы ngx-translate, связанные с фолбэком, чтобы они не растекались
 * по приложению:
 *
 * 1. Словари цепочки грузятся заранее, потому что `FallbackChainMissingTranslationHandler`
 *    резолвит ключи синхронно.
 * 2. `setDefaultLang` вызывается только после загрузки цепочки. К этому моменту словарь уже
 *    в кэше, поэтому вызов срабатывает мгновенно и порождает `onDefaultLangChange` — единственное
 *    событие, на которое пайпы и директивы пересчитываются независимо от языка. Так ключи,
 *    отрисованные до готовности цепочки, заменяются переводом ровно один раз.
 */
@Injectable({ providedIn: 'root' })
export class TranslationFallbackService {
  private readonly _requestedLangs = new Set<string>();

  constructor(
    private readonly _translate: TranslateService,
    private readonly _loader: TranslateLoader,
    @Inject(TRANSLATION_FALLBACK_LANGS) private readonly _fallbackLangs: readonly string[],
  ) {}

  /**
   * Загружает словари цепочки фолбэка и включает первое доступное её звено как `defaultLang`.
   *
   * Вызывать после `translate.use()`: первый рендер не блокируется, цепочка догружается
   * параллельно.
   *
   * @param availableLangs языки, доступные на бэкенде. Отсутствующие звенья цепочки пропускаются.
   * @returns Observable, который завершается когда fallback-загрузка завершена (или синхронно, если нечего грузить).
   */
  activate(availableLangs: readonly string[]): Observable<void> {
    const langsToLoad = this._fallbackLangs.filter((lang) =>
      this._shouldLoad(lang, availableLangs),
    );

    if (!langsToLoad.length) {
      this._activateFallbackLang(availableLangs, false);
      return of(undefined);
    }

    return forkJoin(langsToLoad.map((lang) => this._loadLang(lang))).pipe(
      take(1),
      map(() => {
        this._activateFallbackLang(availableLangs, true);
      }),
    );
  }

  private _shouldLoad(lang: string, availableLangs: readonly string[]): boolean {
    return (
      availableLangs.includes(lang) &&
      !this._requestedLangs.has(lang) &&
      !this._translate.translations[lang]
    );
  }

  private _loadLang(lang: string): Observable<void> {
    this._requestedLangs.add(lang);

    return this._loader.getTranslation(lang).pipe(
      take(1),
      map((translations) => {
        // MultiTranslateHttpLoader гасит сетевые ошибки и отдаёт `{}`. Пустой словарь в кэше
        // считался бы загруженным и навсегда заблокировал бы повторную попытку.
        if (!translations || !Object.keys(translations).length) {
          this._requestedLangs.delete(lang);
          return;
        }
        this._translate.setTranslation(lang, translations as TranslationObject, true);
      }),
      catchError(() => {
        this._requestedLangs.delete(lang);
        return of(undefined);
      }),
    );
  }

  /**
   * @param didLoadDictionaries были ли догружены новые словари. Если нет, пересчитывать
   * переводы не требуется — в кэше не появилось ничего, что изменило бы результат.
   */
  private _activateFallbackLang(
    availableLangs: readonly string[],
    didLoadDictionaries: boolean,
  ): void {
    const fallbackLang = this._fallbackLangs.find((lang) => availableLangs.includes(lang));

    if (!fallbackLang) {
      return;
    }

    if (this._translate.defaultLang !== fallbackLang) {
      // `setDefaultLang` сам порождает `onDefaultLangChange`, на который пайпы и директивы
      // пересчитываются независимо от языка. Отдельное уведомление не нужно.
      this._translate.setDefaultLang(fallbackLang);
      return;
    }

    if (didLoadDictionaries) {
      this._notifyTranslationsChanged();
    }
  }

  /**
   * Сообщает ngx-translate, что резолв переводов текущего языка мог измениться.
   *
   * Нужно в единственном случае: словари цепочки догружены, но `defaultLang` уже совпадал
   * с нужным, поэтому `setDefaultLang` вышел бы без изменений и не уведомил подписчиков.
   * `TranslatePipe` реагирует на `onTranslationChange` только для текущего языка, поэтому
   * событие публикуется именно для него — пустым merge, без изменения самих переводов.
   */
  private _notifyTranslationsChanged(): void {
    const currentLang = this._translate.currentLang;

    if (currentLang && this._translate.translations[currentLang]) {
      this._translate.setTranslation(currentLang, {}, true);
    }
  }
}
